import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Input, Divider, Chip, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTemplate } from '../contexts/TemplateContext';
import { SafeDynamicTemplateRenderer } from '@circuitry-ai/doc-ui';
import { GeneratedTemplate } from '../../modules/shared/types/src/lib/template-types';
import { useLocation } from 'react-router-dom';

const ClaimROViewer = () => {
    const templateContext = useTemplate();
    const location = useLocation();
    const navState = location.state as { templateApplied?: string } | undefined;

    // View mode state
    const [viewMode, setViewMode] = useState<'default' | 'custom'>('default');
    const [selectedTemplate, setSelectedTemplate] = useState<GeneratedTemplate | null>(null);

    // Load templates on mount
    useEffect(() => {
        templateContext.loadTemplates();
    }, []);

    // Apply template if coming from UXGenie
    useEffect(() => {
        if (navState?.templateApplied && templateContext.templates.length > 0) {
            const template = templateContext.templates.find(t => t.metadata.id === navState.templateApplied);
            if (template) {
                setSelectedTemplate(template);
                setViewMode('custom');
            }
        }
    }, [navState, templateContext.templates]);

    // Sample data
    const data = {
        "claim_close_date": null,
        "product": {
            "product_base_model": null,
            "product_in_service_date": "2023-11-24T00:00:00",
            "product_id": "5TDAAAB6XRS002518",
            "product_in_service_mileage": null,
            "product_model_varient": "CAR",
            "product_manufactured_date": "2023-11-24T00:00:00",
            "actor_id": null,
            "product_name": "LEXUS",
            "product_model": "TX 350 Base",
            "product_serial_no": "5TDAAAB6XRS002518"
        },
        "claim_status": "Paid",
        "claim_id": null,
        "policies": {
            "policy_coverage_code": "BUND",
            "policy_type": null,
            "policy_id": "WBUND026313",
            "policy_coverage": "Bundle",
            "policy_end_date": "2029-11-24T00:00:00",
            "product_id": "5TDAAAB6XRS002518",
            "policy_description": "T&W/PDR Bundle Premium Plus",
            "policy_max_operating_hours": null,
            "policy_inservice_mileage": 16,
            "policy_max_mileage": 1000015,
            "policy_term": 72,
            "policy_in_service_date": "2023-11-24T00:00:00"
        },
        "claim_type": null,
        "requests": [
            {
                "request_status": "Paid",
                "request_product_in_service_hours": null,
                "failure_date": "2025-02-28T00:00:00",
                "complaint_description": "PASS FRONT TIRE KEEPS GOING LOW",
                "corrective_action_description": "REPLACE TIRE ",
                "policy_id": "WBUND026313",
                "failure_loss_code": "TR1",
                "claim_id": "CL12902670",
                "corrective_action_code": null,
                "cause_code": null,
                "failure_loss_code_description": "Right Front Tire Replacement",
                "labor": [
                    {
                        "total_denied": 0.0,
                        "status_code": "Paid",
                        "labour_per_hour_cost_requested": 29.85,
                        "total_pending": 0.0,
                        "labour_description": null,
                        "detail_type": "Labor",
                        "claim_id": "CL12902670",
                        "labour_tax_amount1": 0.0,
                        "labour_adjusted_hours": 1.0,
                        "labour_tax_amount2": 0.0,
                        "labour_final_cost": 29.85,
                        "labour_type": null,
                        "amount_id": null,
                        "labour_requested_hours": 1.0,
                        "is_sublet": false,
                        "total_paid": 29.85,
                        "loss_code_description": "Right Front Tire Replacement",
                        "labour_id": 9631373,
                        "is_goodwill": false,
                        "labour_per_hour_cost_adjusted": 29.85,
                        "request_id": 1073050,
                        "labour_op_code": null,
                        "loss_code": "TR1"
                    }
                ],
                "request_product_in_service_mileage": 18215,
                "repair_date": "2025-02-28T00:00:00",
                "cause_description": "HOLE IN TIRE",
                "complaint_code": null,
                "request_product_in_service_days": null,
                "product_id": null,
                "parts": [
                    {
                        "total_denied": 0.0,
                        "total_pending": 0.0,
                        "part_description": "OEA 255/55R20 GY",
                        "parts_status_reason": null,
                        "claim_id": "CL12902670",
                        "part_quantity_rejected": null,
                        "part_detail_type": "Part",
                        "part_category_description": null,
                        "part_status_code": "Paid",
                        "is_sublet": false,
                        "total_paid": 241.5,
                        "part_quantity_requested": 1.0,
                        "loss_code_description": "Right Front Tire Replacement",
                        "msrp": 268.0,
                        "part_id": 9631372,
                        "per_part_cost_requested": 241.5,
                        "parts_tax_amount2": 0.0,
                        "part_code": null,
                        "loss_code": "TR1",
                        "parts_tax_amount1": 0.0,
                        "per_part_cost_adjusted": 241.5,
                        "part_quantity_adjusted": 1.0,
                        "part_category": "Part",
                        "part_status_description": "Paid",
                        "coverage_loss_code": null,
                        "part_sub_category": null,
                        "amount_id": null,
                        "final_part_cost": 241.5,
                        "part_code_description": null,
                        "part_number": "DT06856566",
                        "is_goodwill": false,
                        "request_id": 1073050,
                        "part_name": "OEA 255/55R20 GY"
                    },
                    {
                        "total_denied": 0.0,
                        "total_pending": 0.0,
                        "part_description": null,
                        "parts_status_reason": null,
                        "claim_id": "CL12902670",
                        "part_quantity_rejected": null,
                        "part_detail_type": "Part",
                        "part_category_description": null,
                        "part_status_code": "Paid",
                        "is_sublet": false,
                        "total_paid": 5.84,
                        "part_quantity_requested": 1.0,
                        "loss_code_description": "Valve Stem",
                        "msrp": 0.0,
                        "part_id": 9631374,
                        "per_part_cost_requested": 5.84,
                        "parts_tax_amount2": 0.0,
                        "part_code": null,
                        "loss_code": "TWValve",
                        "parts_tax_amount1": 0.0,
                        "per_part_cost_adjusted": 5.84,
                        "part_quantity_adjusted": 1.0,
                        "part_category": "Part",
                        "part_status_description": "Paid",
                        "coverage_loss_code": null,
                        "part_sub_category": null,
                        "amount_id": null,
                        "final_part_cost": 5.84,
                        "part_code_description": null,
                        "part_number": null,
                        "is_goodwill": false,
                        "request_id": 1073050,
                        "part_name": null
                    }
                ],
                "parts_id": 9631372,
                "request_id": 1073050,
                "request_amounts": [
                    {
                        "amount_type": "claim",
                        "amount_paid": 277.19,
                        "amount_paid_without_tax": 277.19,
                        "amount_requested": 277.19,
                        "claim_id": null,
                        "amount_adjusted": 277.19,
                        "request_id": 1073050,
                        "amount_id": null
                    },
                    {
                        "amount_type": "part",
                        "amount_paid": 247.34,
                        "amount_paid_without_tax": 247.34,
                        "amount_requested": 247.34,
                        "claim_id": null,
                        "amount_adjusted": 247.34,
                        "request_id": 1073050,
                        "amount_id": null
                    },
                    {
                        "amount_type": "labour",
                        "amount_paid": 29.85,
                        "amount_paid_without_tax": 29.85,
                        "amount_requested": 29.85,
                        "claim_id": null,
                        "amount_adjusted": 29.85,
                        "request_id": 1073050,
                        "amount_id": null
                    },
                    {
                        "amount_type": "other",
                        "amount_paid": 0,
                        "amount_paid_without_tax": 0,
                        "amount_requested": 0,
                        "claim_id": null,
                        "amount_adjusted": 0,
                        "request_id": 1073050,
                        "amount_id": null
                    }
                ]
            }
        ],
        "actors": [
            {
                "alter_name": "Phuoc Le",
                "actor_type": "dealer",
                "servicer_type": "Dealer",
                "actor_phone": null,
                "actor_state": "KS",
                "claim_id": "CL12902670",
                "actor_name": "LEXUS OF WICHITA",
                "actor_address": "1555 N GREENWICH RD",
                "actor_zip_code": "67206",
                "actor_id": 6887770,
                "actor_city": "WICHITA",
                "actor_code": "W19"
            },
            {
                "alter_name": "Phuoc Le",
                "actor_type": "customer",
                "servicer_type": null,
                "actor_phone": "3164616747",
                "actor_state": "KS",
                "claim_id": "CL12902670",
                "actor_name": "ANH LE",
                "actor_address": "12425 E Zimmerly Ct, Wichita, KS 67207, USA",
                "actor_zip_code": "67207",
                "actor_id": null,
                "actor_city": "Wichita",
                "actor_code": null
            }
        ],
        "claim_no": "CL12902670",
        "claim_open_date": "2025-02-28T00:00:00",
        "claim_RO_number": "301667",
        "claim_first_time_pay_rate": null,
        "claim_last_updated_date": "2025-03-06T05:00:04.937",
        "claim_adjuster_id": "MICK FINNERTY",
        "claim_date": "2025-02-28T00:00:00",
        "others": [],
        "claim_amounts": [
            {
                "amount_type": "claim",
                "amount_requested": 277.19,
                "amount_adjusted": 277.19,
                "amount_denied": 0,
                "amount_submitted": 277.19,
                "amount_total_paid": 277.19
            },
            {
                "amount_type": "part",
                "amount_requested": 247.34,
                "amount_adjusted": 247.34,
                "amount_denied": 0,
                "amount_submitted": 247.34,
                "amount_total_paid": 247.34
            },
            {
                "amount_type": "labour",
                "amount_requested": 29.85,
                "amount_adjusted": 29.85,
                "amount_denied": 0,
                "amount_submitted": 29.85,
                "amount_total_paid": 29.85
            },
            {
                "amount_type": "other",
                "deductible_paid_amount": 0,
                "amount_requested": 0,
                "amount_adjusted": 0,
                "amount_denied": 0,
                "amount_submitted": 0,
                "deductible_type": "None",
                "amount_total_paid": 0,
                "deductible_amount": 0
            }
        ]
    };

    // Handler functions
    const handleGenerateTemplate = () => {
        templateContext.openUXGenie(data, undefined, '/claimroviewer');
    };

    const handleTemplateSelect = (templateId: string) => {
        const template = templateContext.templates.find(t => t.metadata.id === templateId);
        if (template) {
            setSelectedTemplate(template);
            setViewMode('custom');
        }
    };

    const handleToggleViewMode = () => {
        setViewMode(viewMode === 'default' ? 'custom' : 'default');
    };

    // Default view component
    const DefaultClaimView = () => (
        <div className="flex flex-col gap-6">{/* Header Section */}
            {/* Header Section */}
            <Card className="w-full">
                <CardHeader className="flex justify-between items-center">
                    <div className="flex flex-col">
                        <h1 className="text-2xl font-bold text-default-700">Claim #{data.claim_no}</h1>
                        <p className="text-sm text-default-500">RO Number: {data.claim_RO_number}</p>
                    </div>
                    <Chip color={data.claim_status === 'Paid' ? 'success' : 'warning'} variant="flat" size="lg">
                        {data.claim_status}
                    </Chip>
                </CardHeader>
                <CardBody className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Input label="Open Date" value={new Date(data.claim_open_date).toLocaleDateString()} isReadOnly variant="bordered" />
                    <Input label="Claim Date" value={new Date(data.claim_date).toLocaleDateString()} isReadOnly variant="bordered" />
                    <Input label="Last Updated" value={new Date(data.claim_last_updated_date).toLocaleDateString()} isReadOnly variant="bordered" />
                    <Input label="Adjuster" value={data.claim_adjuster_id} isReadOnly variant="bordered" />
                </CardBody>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Product Information */}
                <Card>
                    <CardHeader>
                        <h2 className="text-xl font-semibold">Vehicle Information</h2>
                    </CardHeader>
                    <Divider />
                    <CardBody className="grid grid-cols-2 gap-4">
                        <Input label="Product Name" value={data.product.product_name} isReadOnly variant="bordered" />
                        <Input label="Model" value={data.product.product_model} isReadOnly variant="bordered" />
                        <Input label="Serial No" value={data.product.product_serial_no} isReadOnly variant="bordered" />
                        <Input label="In-Service Date" value={new Date(data.product.product_in_service_date).toLocaleDateString()} isReadOnly variant="bordered" />
                    </CardBody>
                </Card>

                {/* Policy Information */}
                <Card>
                    <CardHeader>
                        <h2 className="text-xl font-semibold">Policy Information</h2>
                    </CardHeader>
                    <Divider />
                    <CardBody className="grid grid-cols-2 gap-4">
                        <Input label="Policy ID" value={data.policies.policy_id} isReadOnly variant="bordered" />
                        <Input label="Coverage" value={data.policies.policy_coverage} isReadOnly variant="bordered" />
                        <Input label="Description" value={data.policies.policy_description} isReadOnly variant="bordered" className="col-span-2" />
                        <Input label="End Date" value={new Date(data.policies.policy_end_date).toLocaleDateString()} isReadOnly variant="bordered" />
                        <Input label="Term" value={`${data.policies.policy_term} Months`} isReadOnly variant="bordered" />
                    </CardBody>
                </Card>
            </div>

            {/* Requests Section */}
            <h2 className="text-2xl font-bold text-default-700 mt-4">Requests</h2>
            {data.requests.map((request, index) => (
                <Card key={index} className="w-full">
                    <CardHeader className="flex flex-col items-start gap-2">
                        <div className="flex justify-between w-full">
                            <h3 className="text-lg font-semibold">Request #{index + 1} - {request.failure_loss_code_description}</h3>
                            <Chip size="sm" color={request.request_status === 'Paid' ? 'success' : 'default'}>{request.request_status}</Chip>
                        </div>
                        <p className="text-sm text-default-500">Complaint: {request.complaint_description}</p>
                        <p className="text-sm text-default-500">Corrective Action: {request.corrective_action_description}</p>
                    </CardHeader>
                    <Divider />
                    <CardBody className="flex flex-col gap-6">
                        {/* Labor Table */}
                        <div>
                            <h4 className="text-md font-semibold mb-2">Labor</h4>
                            <Table aria-label="Labor details">
                                <TableHeader>
                                    <TableColumn>DESCRIPTION</TableColumn>
                                    <TableColumn>HOURS</TableColumn>
                                    <TableColumn>RATE</TableColumn>
                                    <TableColumn>TOTAL</TableColumn>
                                    <TableColumn>STATUS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {request.labor.map((lab, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{lab.loss_code_description}</TableCell>
                                            <TableCell>{lab.labour_adjusted_hours}</TableCell>
                                            <TableCell>${lab.labour_per_hour_cost_adjusted}</TableCell>
                                            <TableCell>${lab.labour_final_cost}</TableCell>
                                            <TableCell><Chip size="sm" variant="flat" color="success">{lab.status_code}</Chip></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Parts Table */}
                        <div>
                            <h4 className="text-md font-semibold mb-2">Parts</h4>
                            <Table aria-label="Parts details">
                                <TableHeader>
                                    <TableColumn>PART NAME</TableColumn>
                                    <TableColumn>PART NUMBER</TableColumn>
                                    <TableColumn>QTY</TableColumn>
                                    <TableColumn>COST</TableColumn>
                                    <TableColumn>STATUS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {request.parts.map((part, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{part.part_description || part.part_name || 'N/A'}</TableCell>
                                            <TableCell>{part.part_number || 'N/A'}</TableCell>
                                            <TableCell>{part.part_quantity_adjusted}</TableCell>
                                            <TableCell>${part.final_part_cost}</TableCell>
                                            <TableCell><Chip size="sm" variant="flat" color="success">{part.part_status_code}</Chip></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            ))}

            {/* Financials & Actors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Financial Summary */}
                <Card>
                    <CardHeader>
                        <h2 className="text-xl font-semibold">Financial Summary</h2>
                    </CardHeader>
                    <Divider />
                    <CardBody>
                        <Table aria-label="Financial summary" removeWrapper>
                            <TableHeader>
                                <TableColumn>TYPE</TableColumn>
                                <TableColumn>REQUESTED</TableColumn>
                                <TableColumn>PAID</TableColumn>
                            </TableHeader>
                            <TableBody>
                                {data.claim_amounts.map((amount, i) => (
                                    <TableRow key={i}>
                                        <TableCell className="capitalize">{amount.amount_type}</TableCell>
                                        <TableCell>${amount.amount_requested}</TableCell>
                                        <TableCell className="font-bold text-green-600">${amount.amount_total_paid}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Actors */}
                <Card>
                    <CardHeader>
                        <h2 className="text-xl font-semibold">Involved Parties</h2>
                    </CardHeader>
                    <Divider />
                    <CardBody className="flex flex-col gap-4">
                        {data.actors.map((actor, i) => (
                            <div key={i} className="flex flex-col gap-1 p-3 border rounded-lg">
                                <div className="flex justify-between">
                                    <span className="font-semibold">{actor.actor_name}</span>
                                    <Chip size="sm" variant="dot" color="primary" className="capitalize">{actor.actor_type}</Chip>
                                </div>
                                <span className="text-sm text-default-500">{actor.actor_address}, {actor.actor_city}, {actor.actor_state} {actor.actor_zip_code}</span>
                                {actor.actor_phone && <span className="text-sm text-default-500">Phone: {actor.actor_phone}</span>}
                            </div>
                        ))}
                    </CardBody>
                </Card>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-6 p-6 bg-default-100 min-h-screen">
            {/* Template Controls */}
            <Card className="w-full">
                <CardHeader>
                    <div className="flex justify-between items-center w-full">
                        <div className="flex items-center gap-3">
                            <Icon icon="mdi:view-dashboard" className="w-6 h-6 text-blue-600" />
                            <div>
                                <h2 className="text-xl font-bold">Claim RO Viewer</h2>
                                <p className="text-sm text-default-500">
                                    {viewMode === 'custom' && selectedTemplate
                                        ? `Using template: ${selectedTemplate.metadata.name}`
                                        : 'Default view'}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="bordered"
                                startContent={<Icon icon="material-symbols:add" />}
                                onPress={handleGenerateTemplate}
                            >
                                Generate Template
                            </Button>
                            {templateContext.templates.length > 0 && (
                                <Select
                                    label="Select Template"
                                    placeholder="Choose a template"
                                    size="sm"
                                    className="w-64"
                                    selectedKeys={selectedTemplate ? [selectedTemplate.metadata.id] : []}
                                    onChange={(e) => handleTemplateSelect(e.target.value)}
                                >
                                    {templateContext.templates.map((template) => (
                                        <SelectItem key={template.metadata.id} value={template.metadata.id}>
                                            {template.metadata.name}
                                        </SelectItem>
                                    ))}
                                </Select>
                            )}
                            {selectedTemplate && (
                                <Button
                                    size="sm"
                                    color={viewMode === 'custom' ? 'primary' : 'default'}
                                    variant={viewMode === 'custom' ? 'solid' : 'bordered'}
                                    startContent={
                                        <Icon
                                            icon={
                                                viewMode === 'custom'
                                                    ? 'material-symbols:view-compact'
                                                    : 'material-symbols:view-list'
                                            }
                                        />
                                    }
                                    onPress={handleToggleViewMode}
                                >
                                    {viewMode === 'custom' ? 'Custom View' : 'Default View'}
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>
            </Card>

            {/* Render based on view mode */}
            {viewMode === 'custom' && selectedTemplate ? (
                <SafeDynamicTemplateRenderer
                    template={selectedTemplate}
                    data={data}
                    fallback={<DefaultClaimView />}
                    onError={(error) => {
                        console.error('Template rendering error:', error);
                        alert(`Template error: ${error.message}. Switching to default view.`);
                        setViewMode('default');
                    }}
                />
            ) : (
                <DefaultClaimView />
            )}
        </div>
    );
};

export default ClaimROViewer;
